import {
  BanknoteIcon,
  CalendarIcon,
  CheckCircle2Icon,
  CircleCheckIcon,
  CreditCardIcon,
  DownloadIcon,
  ExternalLinkIcon,
  GiftIcon,
  LandmarkIcon,
  MoreVerticalIcon,
  QrCodeIcon,
  ReceiptIcon,
  StarIcon,
  Undo2Icon,
  WalletIcon,
  XCircleIcon,
} from "lucide-react"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import { Button } from "@/components/ui/button"
import { DataTable } from "@/components/ui/data-table"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Separator } from "@/components/ui/separator"
import { usePermission } from "@/contexts/permission"
import {
  PaymentMethodNameEnum,
  PermissionsRolesEnum,
} from "@/enums"
import type { CycleStatusEnum } from "@/enums/subscriptions"
import { getPaymentMethod } from "@/helpers/defaulters/get-payment-method"
import { formatMoney } from "@/helpers/format"
import { DateHelper } from "@/utils"

import { CycleEventsSheet } from "./cycle-events-sheet"
import { CycleStatusBadge } from "./cycle-status-badge"
import { SubscriptionEventTimeline } from "./event-timeline"
import { RefundSubscriptionDialog } from "./refund-subscription-dialog"
import { ReverseCycleDialog } from "./reverse-cycle-dialog"
import { createSubscriptionsDetailsColumns } from "./subscriptions-details-columns"
import {
  canReverseCycle,
  isCancelPaymentMethodByType,
  SUBSCRIPTION_TYPE_VTEX,
} from "./SubscriptionsHelper"

const PAYMENT_METHOD_ICONS: Record<
  PaymentMethodNameEnum,
  React.ElementType
> = {
  [PaymentMethodNameEnum.BALANCE]: WalletIcon,
  [PaymentMethodNameEnum.CREDIT_CARD]: CreditCardIcon,
  [PaymentMethodNameEnum.DEBIT_CARD]: CreditCardIcon,
  [PaymentMethodNameEnum.LOYALTY_POINTS]: StarIcon,
  [PaymentMethodNameEnum.GIFT_CARD_VTEX]: GiftIcon,
  [PaymentMethodNameEnum.OPEN_FINANCE]: LandmarkIcon,
  [PaymentMethodNameEnum.BANK_SLIP]: ReceiptIcon,
  [PaymentMethodNameEnum.BOLEPIX]: ReceiptIcon,
  [PaymentMethodNameEnum.DIRECT_DEBIT]: LandmarkIcon,
  [PaymentMethodNameEnum.NUPAY]: BanknoteIcon,
  [PaymentMethodNameEnum.PIX]: QrCodeIcon,
  [PaymentMethodNameEnum.PIX_AUTO]: QrCodeIcon,
  [PaymentMethodNameEnum.CREDIT_CARD_PRE_APPROVAL]:
    CreditCardIcon,
}

interface CyclesTableProps {
  cycles: SubscriptionDetailsColumnsType[]
}

export const CyclesTable = ({
  cycles,
}: CyclesTableProps) => {
  const { t } = useTranslation()
  const { hasPermission } = usePermission()
  const [refundDialogState, setRefundDialogState] =
    useState<{
      open: boolean
      cycleId: string | null
      billingCycle: number | null
      paymentMethod: PaymentMethodNameEnum | null
    }>({
      open: false,
      cycleId: null,
      billingCycle: null,
      paymentMethod: null,
    })
  const [reversalDialogState, setReversalDialogState] =
    useState<{
      open: boolean
      subscriptionId: string | null
      cycleId: string | null
    }>({
      open: false,
      subscriptionId: null,
      cycleId: null,
    })

  const canWriteSubscriptions = hasPermission([
    PermissionsRolesEnum.SUBSCRIPTIONS_WRITE,
  ])

  const columns = createSubscriptionsDetailsColumns(
    t,
    hasPermission
  )

  const totalAmount = cycles.reduce(
    (sum, cycle) => sum + cycle.totalAmount,
    0
  )

  return (
    <>
      {/* Mobile: Card layout */}
      <div className="space-y-4 lg:hidden">
        {cycles.map((cycle) => (
          <div
            key={cycle.id}
            className="group overflow-hidden rounded-xl border border-border bg-card shadow-sm transition-all hover:shadow-md"
          >
            <div className="p-4 sm:p-5">
              <div className="mb-4 flex items-center gap-3">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-primary/10">
                  <CalendarIcon
                    size={20}
                    className="text-primary"
                  />
                </div>
                <div className="flex flex-col">
                  <span className="text-xs font-medium text-muted-foreground">
                    {t("subscriptions.table.cycle")}
                  </span>
                  <span className="text-lg font-semibold">
                    #{cycle.billingCycle}
                  </span>
                </div>
              </div>

              <div className="mb-4 space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  {cycle.processingDate && (
                    <div className="rounded-lg border border-border/50 bg-muted/20 p-3">
                      <p className="mb-1 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                        {t(
                          "subscriptions.table.processingDate"
                        )}
                      </p>
                      <p className="text-sm font-semibold">
                        {DateHelper.formatDate(
                          cycle.processingDate,
                          "dd/MM/yyyy"
                        )}
                      </p>
                    </div>
                  )}
                  {cycle.dueDate && (
                    <div className="rounded-lg border border-border/50 bg-muted/20 p-3">
                      <p className="mb-1 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                        {t("subscriptions.table.dueDate")}
                      </p>
                      <p className="text-sm font-semibold">
                        {DateHelper.formatDate(
                          cycle.dueDate,
                          "dd/MM/yyyy"
                        )}
                      </p>
                    </div>
                  )}
                </div>

                {/* Status */}
                <div className="flex items-center gap-2 rounded-lg border border-border/50 bg-muted/20 p-3">
                  <span className="text-xs font-medium text-muted-foreground">
                    {t("common.status")}:
                  </span>
                  <CycleStatusBadge
                    status={cycle.status as CycleStatusEnum}
                    paymentMethod={
                      cycle.paymentInstrument
                        ?.type as PaymentMethodNameEnum
                    }
                  />
                </div>

                {/* Order Link */}
                {cycle.order?.code && (
                  <div className="flex items-center gap-2 rounded-lg bg-muted/30 px-3 py-2">
                    <span className="text-xs font-medium text-muted-foreground">
                      {t("common.order")}:
                    </span>
                    <a
                      href={`/orders/details/${cycle.orderId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 font-mono text-xs font-semibold text-primary hover:underline"
                    >
                      {cycle.order.code}
                      <ExternalLinkIcon size={12} />
                    </a>
                  </div>
                )}
                {cycle.paymentInstrument?.type && (
                  <div className="rounded-lg bg-muted/30 px-3 py-2">
                    <p className="mb-2 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                      {t("common.paymentMethod")}
                    </p>
                    <div className="flex items-center gap-2.5">
                      {(() => {
                        const Icon =
                          PAYMENT_METHOD_ICONS[
                            cycle.paymentInstrument
                              ?.type as PaymentMethodNameEnum
                          ]
                        return Icon ? (
                          <div className="flex h-8 w-8 items-center justify-center rounded-md bg-background">
                            <Icon
                              size={16}
                              className="shrink-0 text-primary"
                            />
                          </div>
                        ) : null
                      })()}
                      <div className="flex flex-col gap-1">
                        <span className="text-sm font-semibold">
                          {getPaymentMethod(
                            cycle.paymentInstrument
                              ?.type as PaymentMethodNameEnum,
                            t
                          )}
                        </span>
                        {cycle.paymentInstrument?.type ===
                          PaymentMethodNameEnum.CREDIT_CARD &&
                          cycle.paymentInstrument?.card && (
                            <div className="flex items-center gap-1.5">
                              {cycle.paymentInstrument.card
                                .brand?.logoUrl && (
                                <img
                                  src={
                                    cycle.paymentInstrument
                                      .card.brand.logoUrl
                                  }
                                  alt={
                                    cycle.paymentInstrument
                                      .card.brand?.name ||
                                    "card"
                                  }
                                  className="h-4 w-auto object-contain"
                                />
                              )}
                              {cycle.paymentInstrument.card
                                .lastFourDigits && (
                                <span className="text-xs font-medium text-muted-foreground">
                                  {t("common.lastDigits")}:{" "}
                                  {
                                    cycle.paymentInstrument
                                      .card.lastFourDigits
                                  }
                                </span>
                              )}
                            </div>
                          )}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <Separator className="mt-3" />

              {/* Processor Information */}
              {cycle.paymentInstruments?.[0]
                ?.processorInformation && (
                <div className="mt-3 rounded-lg bg-muted/20 p-3">
                  <div className="mb-2 flex items-center gap-2">
                    <CheckCircle2Icon
                      size={14}
                      className="text-green-600"
                    />
                    <span className="text-xs font-semibold text-muted-foreground">
                      {t("common.processorInfo")}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    {cycle.paymentInstruments[0]
                      .processorInformation.name && (
                      <div>
                        <span className="text-muted-foreground">
                          {t("common.acquirer")}:
                        </span>
                        <span className="ml-1 font-semibold">
                          {
                            cycle.paymentInstruments[0]
                              .processorInformation.name
                          }
                        </span>
                      </div>
                    )}
                    {cycle.paymentInstruments[0]
                      .processorInformation.authCode && (
                      <div>
                        <span className="text-muted-foreground">
                          {t("common.authCode")}:
                        </span>
                        <code className="ml-1 font-mono font-semibold">
                          {
                            cycle.paymentInstruments[0]
                              .processorInformation.authCode
                          }
                        </code>
                      </div>
                    )}
                    {cycle.paymentInstruments[0]
                      .processorInformation.nsu && (
                      <div className="col-span-2">
                        <span className="text-muted-foreground">
                          NSU:
                        </span>
                        <code className="ml-1 font-mono text-[10px] font-semibold">
                          {
                            cycle.paymentInstruments[0]
                              .processorInformation.nsu
                          }
                        </code>
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="mt-3 flex flex-col gap-3 border-t pt-4 sm:flex-row sm:items-end sm:justify-between">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:gap-4">
                  <div className="flex flex-col gap-1">
                    <span className="text-xs font-medium text-muted-foreground">
                      {t("common.value")}
                    </span>
                    <span className="text-xl font-bold tabular-nums text-primary">
                      {formatMoney(cycle.totalAmount / 100)}
                    </span>
                    <div className="flex flex-col gap-0.5 text-[9px] text-muted-foreground">
                      <span>
                        {t("common.baseAmount")}:{" "}
                        {formatMoney(
                          (cycle.amount || 0) / 100
                        )}
                      </span>
                      {(cycle.discount || 0) > 0 && (
                        <span>
                          {t("common.discount")}: -
                          {formatMoney(
                            (cycle.discount || 0) / 100
                          )}
                        </span>
                      )}
                      {(cycle.interest || 0) > 0 && (
                        <span>
                          {t("common.interest")}: +
                          {formatMoney(
                            (cycle.interest || 0) / 100
                          )}
                        </span>
                      )}
                    </div>
                  </div>
                  {cycle.paidAt && (
                    <div className="flex flex-col">
                      <span className="text-[10px] font-medium text-muted-foreground">
                        {t("common.paidAt")}
                      </span>
                      <div className="flex items-center gap-1">
                        <CheckCircle2Icon
                          size={12}
                          className="text-green-600"
                        />
                        <span className="text-xs font-semibold text-green-600">
                          {DateHelper.format(
                            cycle.paidAt,
                            "dd/MM/yyyy HH:mm"
                          )}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-9 w-9 p-0"
                      >
                        <MoreVerticalIcon className="h-4 w-4" />
                        <span className="sr-only">
                          {t("common.actions")}
                        </span>
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent
                      align="end"
                      className="w-[200px]"
                    >
                      <DropdownMenuLabel>
                        {t("common.actions")}
                      </DropdownMenuLabel>
                      <DropdownMenuSeparator />
                      {cycle.order?.code && (
                        <DropdownMenuItem asChild>
                          <a
                            href={`/orders/details/${cycle.orderId}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex cursor-pointer items-center gap-2"
                          >
                            <ExternalLinkIcon size={14} />
                            <span>
                              {t("common.orderDetails")}
                            </span>
                          </a>
                        </DropdownMenuItem>
                      )}
                      {cycle.order?.receiptUrls?.pdf && (
                        <DropdownMenuItem
                          onClick={() =>
                            window.open(
                              cycle.order.receiptUrls!.pdf,
                              "_blank"
                            )
                          }
                          className="flex cursor-pointer items-center gap-2"
                        >
                          <DownloadIcon size={14} />
                          <span>{t("common.receipt")}</span>
                        </DropdownMenuItem>
                      )}
                      {(() => {
                        const isVTEX =
                          cycle.subscriptionType ===
                          SUBSCRIPTION_TYPE_VTEX
                        const status =
                          cycle.order?.statusDetail?.status
                        const canRefund = hasPermission([
                          PermissionsRolesEnum.SUBSCRIPTIONS_WRITE,
                        ])
                        const isRefundable =
                          ![
                            "PROCESSING_PAYMENT",
                            "REFUNDED",
                            "PROCESSING_REFUNDING",
                            "REVERSED",
                          ].includes(status as string) &&
                          !isVTEX &&
                          cycle.paymentId &&
                          canRefund

                        const isCancelPaymentMethod =
                          isCancelPaymentMethodByType(
                            cycle.paymentInstrument
                              ?.type as PaymentMethodNameEnum
                          )

                        const buttonLabel =
                          isCancelPaymentMethod
                            ? "Cancelar"
                            : "Reembolsar"
                        const Icon = isCancelPaymentMethod
                          ? XCircleIcon
                          : CircleCheckIcon

                        return isRefundable ? (
                          <DropdownMenuItem
                            onClick={() =>
                              setRefundDialogState({
                                open: true,
                                cycleId:
                                  cycle.subscriptionId ||
                                  null,
                                billingCycle:
                                  cycle.billingCycle,
                                paymentMethod:
                                  (cycle.paymentInstrument
                                    ?.type as PaymentMethodNameEnum) ||
                                  null,
                              })
                            }
                            className="flex cursor-pointer items-center gap-2 text-destructive focus:text-destructive"
                          >
                            <Icon size={14} />
                            <span>{buttonLabel}</span>
                          </DropdownMenuItem>
                        ) : null
                      })()}
                      {canReverseCycle(
                        {
                          id: cycle.id,
                          subscriptionId: cycle.subscriptionId,
                          orderSubscriptionId:
                            cycle.orderSubscriptionId,
                          subscriptionType:
                            cycle.subscriptionType,
                          paymentId: cycle.paymentId,
                          status: cycle.statusDetail?.status,
                          paymentMethod: cycle
                            .paymentInstrument
                            ?.type as PaymentMethodNameEnum,
                        },
                        canWriteSubscriptions
                      ) && (
                        <DropdownMenuItem
                          className="flex cursor-pointer items-center gap-2 text-destructive focus:text-destructive"
                          onClick={() =>
                            setReversalDialogState({
                              open: true,
                              subscriptionId:
                                cycle.subscriptionId ||
                                cycle.orderSubscriptionId ||
                                null,
                              cycleId: cycle.id,
                            })
                          }
                        >
                          <Undo2Icon size={14} />
                          <span>
                            {t(
                              "subscriptions.cycleReversal.button"
                            )}
                          </span>
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                  <CycleEventsSheet
                    cycleNumber={cycle.billingCycle}
                    events={cycle.events}
                    paymentMethod={
                      cycle.paymentInstrument?.type
                    }
                  />
                </div>
              </div>
            </div>
          </div>
        ))}

        {refundDialogState.open &&
          refundDialogState.cycleId && (
            <RefundSubscriptionDialog
              id={refundDialogState.cycleId}
              billingCycle={refundDialogState.billingCycle!}
              open={refundDialogState.open}
              setOpen={(open) =>
                setRefundDialogState({
                  ...refundDialogState,
                  open,
                })
              }
              paymentMethod={
                refundDialogState.paymentMethod || undefined
              }
            />
          )}

        {reversalDialogState.open &&
          reversalDialogState.subscriptionId &&
          reversalDialogState.cycleId && (
            <ReverseCycleDialog
              cycleId={reversalDialogState.cycleId}
              open={reversalDialogState.open}
              setOpen={(open) =>
                setReversalDialogState({
                  ...reversalDialogState,
                  open,
                })
              }
              subscriptionId={
                reversalDialogState.subscriptionId
              }
            />
          )}

        {cycles.length > 0 && (
          <div className="sticky bottom-0 flex flex-col gap-3 rounded-xl border border-primary/20 bg-gradient-to-br from-primary/5 to-primary/10 p-4 shadow-lg backdrop-blur-sm sm:flex-row sm:items-center sm:justify-between sm:p-5">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/20">
                <CalendarIcon
                  size={20}
                  className="text-primary"
                />
              </div>
              <div className="flex flex-col">
                <span className="text-xs font-medium text-muted-foreground">
                  {t("common.total")}
                </span>
                <span className="text-sm font-semibold">
                  {cycles.length}{" "}
                  {cycles.length === 1
                    ? t("subscriptions.table.cycle")
                    : t("subscriptions.table.cycles")}
                </span>
              </div>
            </div>
            <span className="text-2xl font-bold tabular-nums text-primary">
              {formatMoney(totalAmount / 100)}
            </span>
          </div>
        )}
      </div>

      {/* Desktop: Table layout */}
      <div className="hidden lg:block">
        <div className="overflow-hidden rounded-xl border border-border shadow-sm">
          <DataTable
            columns={columns}
            data={cycles}
            showColumnVisibility
            expandedChildren={({ data }) => (
              <div className="bg-muted/30 p-6">
                <SubscriptionEventTimeline
                  items={data?.events}
                  paymentMethod={
                    data?.paymentInstrument?.type
                  }
                />
              </div>
            )}
          />
        </div>
        {cycles.length > 0 && (
          <div className="mt-4 flex items-center justify-between rounded-xl border border-primary/20 bg-gradient-to-r from-primary/5 to-primary/10 p-5 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/20">
                <CalendarIcon
                  size={20}
                  className="text-primary"
                />
              </div>
              <div className="flex flex-col">
                <span className="text-xs font-medium text-muted-foreground">
                  {t("common.total")}
                </span>
                <span className="text-sm font-semibold">
                  {cycles.length}{" "}
                  {cycles.length === 1
                    ? t("subscriptions.table.cycle")
                    : t("subscriptions.table.cycles")}
                </span>
              </div>
            </div>
            <span className="text-2xl font-bold tabular-nums text-primary">
              {formatMoney(totalAmount / 100)}
            </span>
          </div>
        )}
      </div>
    </>
  )
}
